import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "../shell/AppShell.jsx";
import { useHierarchyTree, findAgent } from "./useHierarchyTree.js";
import DetailDrawer from "../components/DetailDrawer.jsx";
import "./orgchart.css";
import "./hierarchy.css";
import "./superAgentsOverview.css";
// P165: brings the Agent picture card's own styles (wx-sao-agentphoto-*).
import "./superAgentsOverviewPage.css";
import "./superAgentLineOrgChart.css";
import "./plcDetail.css";
import "./agentDetail.css";
import "../pages/states.css";

import PageSkeleton from "../components/Skeleton.jsx";
import AgentPhotoCard from "./AgentPhotoCard.jsx";
const CRUMB_BASE = ["Backoffice", "SuperAdmin", "Super Agents"];
const DRAWER_TITLE_ID = "agent-drawer-title";

/** Static reserved-capability cards for areas with no backing API yet - never invented data, always this exact honest fallback per card. */
const RESERVED_CARDS = [
  {
    key: "workerList",
    title: "แรงงานภายใต้ Agent นี้",
    description: "รายชื่อ รูปโปรไฟล์ ทักษะ และประวัติการทำงานของแรงงานแต่ละคนภายใต้ Agent นี้",
    apiName: "Worker Profile API",
  },
  {
    key: "safety",
    title: "ความปลอดภัย / เหตุการณ์ผิดปกติ",
    description: "เคส SOS, heartbeat หาย, และการออกนอกโซนของแรงงานภายใต้ Agent นี้",
    apiName: "Safety API",
  },
  {
    key: "buddy",
    title: "เพื่อนประกันภัย (Buddy Network)",
    description:
      "แรงงาน active ทุกคนต้องมีเพื่อนประกันครบ 5 คน หากไม่ครบ ระบบต้องแจ้งเตือนเพื่อนประกันและ Agent ทันทีที่เชื่อมต่อ API",
    apiName: "Buddy API",
  },
  {
    key: "footstep",
    title: "Footstep / ประวัติการเดินทาง",
    description: "เส้นทาง โซน และช่วงเวลาที่ไม่มี heartbeat ของแรงงานภายใต้ Agent นี้",
    apiName: "Footstep API",
  },
  {
    key: "contact",
    title: "ข้อมูลติดต่อ Agent",
    description: "เบอร์โทรหลัก เบอร์สำรอง และช่องทางติดต่อของ Agent นี้",
    apiName: "Agent Contact API",
  },
];

/** Invite pipeline cards beyond the real "invited" count - all reserved, never invented. */
const PIPELINE_RESERVED_CARDS = [
  { key: "pipelineQuickRegister", label: "สมัครด่วนแล้ว", apiName: "Application API" },
  { key: "pipelineEkyc", label: "รอ e-KYC", apiName: "e-KYC API" },
  { key: "pipelineReady", label: "พร้อม match งาน", apiName: "Application API" },
];

function cardContentFor(key, agent) {
  if (key === "pipelineInvited") {
    return {
      mark: "I",
      title: "คำเชิญที่สร้างแล้ว (Invite Created)",
      description: `Agent นี้มีคำเชิญที่นับได้จริงจากระบบ ${agent.inviteCount ?? 0} รายการ (นับจากข้อมูลจริงในผังสายงาน) ลิงก์คำเชิญแต่ละรายการและประวัติการส่ง/เปิด/ตอบรับยังไม่เชื่อมต่อ`,
      // Prompt 134 acceptance audit: deliberately a different name from the
      // toolbar's "Create Invite Card" button - that action's own API is
      // real and Agent-only by design (see the toolbar button's own note),
      // while per-invite-link engagement history (sent/opened/responded) is
      // a genuinely separate, still-unbuilt capability. Reusing the same
      // "Invite Card API" label for both would wrongly imply the create
      // action itself is unbuilt.
      apiName: "Invite Link History API",
    };
  }
  if (key === "workerGroups") {
    return {
      mark: "WG",
      title: "กลุ่มแรงงาน (Worker Groups)",
      description: `การจัดกลุ่มแรงงานภายใต้ ${agent.displayName} ตามหน้างาน กะ หรือโซน`,
      apiName: "Worker Group API",
    };
  }
  const pipelineCard = PIPELINE_RESERVED_CARDS.find((card) => card.key === key);
  if (pipelineCard) {
    const descriptions = {
      pipelineQuickRegister: "จำนวนผู้สมัครที่กรอกข้อมูลด่วนผ่านลิงก์ Invite Card แต่ยังไม่ผ่าน e-KYC",
      pipelineEkyc: "ผู้สมัครที่อยู่ระหว่างการยืนยันตัวตนด้วย e-KYC ก่อนจะพร้อม match งาน",
      pipelineReady:
        "ผู้สมัครที่ผ่าน e-KYC แล้วและพร้อมจับคู่งาน การสมัครด่วนเพียงอย่างเดียวยังไม่ถือว่าพร้อม match งาน",
    };
    return { mark: "P", title: pipelineCard.label, description: descriptions[key], apiName: pipelineCard.apiName };
  }
  const reservedCard = RESERVED_CARDS.find((card) => card.key === key);
  if (reservedCard) {
    return { mark: "R", title: reservedCard.title, description: reservedCard.description, apiName: reservedCard.apiName };
  }
  return null;
}

/**
 * /backoffice/super-agents/:superAgentId/plcs/:plcId/agents/:agentId - the
 * Agent detail page, the deepest level of the worker-intake drill-down
 * chain (SuperAdmin -> Super Agent -> PLC/บมจ -> Agent). Agent is the only
 * level where Invite Card creation belongs, so this page shows the single
 * primary "สร้าง Invite Card" entry point - disabled, because the real
 * Invite Card API (Prompt 089R) restricts creation to the owning Agent's
 * own account (InviteCardService.CreateInviteCardAsync), never SuperAdmin
 * browsing this drill-down view. It must never fake-create a working link.
 *
 * Reuses the P086R/P087 drawer pattern exactly: every reserved area
 * (Worker Groups, the invite pipeline's non-"invited" stages, Worker List,
 * Safety, Buddy, Footstep, Contact) is a clickable card that opens a
 * slide-over drawer explaining exactly which API it is waiting on - never a
 * permanent side column, and never invented worker rows/photos/skills.
 * Quick register is explicitly shown as not ready-to-match until e-KYC.
 */
export default function SuperAgentAgentDetailPage() {
  const { superAgentId, plcId, agentId } = useParams();
  const { status, data: tree, error } = useHierarchyTree("worker-intake");
  const [selectedCardKey, setSelectedCardKey] = useState(null);

  if (status === "loading") {
    return (
      <AppShell crumbs={CRUMB_BASE}>
        <PageSkeleton variant="table" toolbar={false} label="กำลังโหลดข้อมูล Agent..." />
      </AppShell>
    );
  }

  if (status === "error") {
    return (
      <AppShell crumbs={CRUMB_BASE}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดข้อมูล Agent ได้</h1>
          <p className="wx-state-body">
            {error?.status === 401
              ? "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่"
              : "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ"}
          </p>
        </div>
      </AppShell>
    );
  }

  const { superAgent, plc, agent } = findAgent(tree, superAgentId, plcId, agentId);

  if (!superAgent) {
    return (
      <AppShell crumbs={CRUMB_BASE}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่พบ Super Agent นี้</h1>
          <p className="wx-state-body">
            Super Agent ที่คุณเปิดอาจถูกย้าย ปิดใช้งาน หรือไม่อยู่ในสิทธิ์การเข้าถึงของคุณ กรุณากลับไปเลือก Super Agent
            จากรายการ
          </p>
          <p className="wx-state-reference">Super Agent ID: {superAgentId}</p>
          <p className="wx-state-contact">
            <Link className="wx-breadcrumb-link" to="/backoffice/super-agents">
              กลับไปหน้ารายชื่อ Super Agent
            </Link>
          </p>
        </div>
      </AppShell>
    );
  }

  if (!plc) {
    return (
      <AppShell crumbs={[...CRUMB_BASE, superAgent.organizationName]}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่พบ PLC / บมจ นี้</h1>
          <p className="wx-state-body">
            PLC / บมจ ที่คุณเปิดอาจถูกย้าย ปิดใช้งาน หรือไม่อยู่ภายใต้ {superAgent.organizationName} กรุณากลับไปเลือก
            PLC / บมจ จากผังสายงาน
          </p>
          <p className="wx-state-reference">PLC ID: {plcId}</p>
          <p className="wx-state-contact">
            <Link className="wx-breadcrumb-link" to={`/backoffice/super-agents/${superAgentId}`}>
              กลับไปผังสายงาน {superAgent.organizationName}
            </Link>
          </p>
        </div>
      </AppShell>
    );
  }

  if (!agent) {
    return (
      <AppShell crumbs={[...CRUMB_BASE, superAgent.organizationName, plc.organizationName]}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่พบ Agent นี้</h1>
          <p className="wx-state-body">
            Agent ที่คุณเปิดอาจถูกย้าย ปิดใช้งาน หรือไม่อยู่ภายใต้ {plc.organizationName} กรุณากลับไปเลือก Agent จากหน้า
            PLC / บมจ
          </p>
          <p className="wx-state-reference">Agent ID: {agentId}</p>
          <p className="wx-state-contact">
            <Link className="wx-breadcrumb-link" to={`/backoffice/super-agents/${superAgentId}/plcs/${plcId}`}>
              กลับไปหน้า PLC / บมจ {plc.organizationName}
            </Link>
          </p>
        </div>
      </AppShell>
    );
  }

  const crumbs = [...CRUMB_BASE, superAgent.organizationName, plc.organizationName, agent.displayName];
  const cardContent = selectedCardKey ? cardContentFor(selectedCardKey, agent) : null;

  return (
    <AppShell crumbs={crumbs}>
      <div className="wx-sa-page">
        <div className="wx-sa-toolbar">
          <label className="wx-sa-search">
            <span className="wx-visually-hidden">Search worker, invite, application</span>
            <input
              type="search"
              placeholder="Search worker, invite, application..."
              disabled
              title="ฟังก์ชันค้นหายังไม่เปิดใช้งาน"
            />
          </label>
          <div className="wx-sa-toolbar-actions wx-agent-toolbar-actions">
            <button type="button" className="wx-button wx-button--ghost" disabled title="ยังไม่เปิดใช้งาน">
              Export
            </button>
            <div className="wx-invite-card-reserved">
              <button
                type="button"
                className="wx-button wx-invite-card-reserved-button"
                disabled
                title="สร้าง Invite Card ได้เฉพาะจากบัญชี Agent เจ้าของเท่านั้น (Agent Portal)"
              >
                สร้าง Invite Card
              </button>
              <span className="wx-invite-card-reserved-note">
                สร้าง Invite Card ได้เฉพาะจากบัญชี Agent เจ้าของเท่านั้น - ไม่ใช่จาก SuperAdmin
              </span>
            </div>
          </div>
        </div>

        <header className="wx-plc-head-row">
          {/* P165: the Agent's own picture, and the way to attach one. Owner:
              "agent ให้แนบรูปได้ด้วยครับ". P161 built the upload; this page was
              the one screen that never showed it, because it renders from the
              worker-intake TREE and the tree carries no picture. */}
          <AgentPhotoCard agentId={agentId} displayName={agent.displayName} />
          <div className="wx-plc-head-text wx-sa-head">
            <h1 className="wx-sa-title">{agent.displayName}</h1>
            <p className="wx-sa-subtitle">
              <strong>{agent.agentCode}</strong> อยู่ภายใต้ {plc.organizationName} · {superAgent.organizationName}
            </p>
          </div>
          <span className="wx-badge wx-badge--info wx-plc-scope-pill">
            Scope: {agent.agentCode} · {agent.workerCount ?? 0} แรงงาน
          </span>
        </header>

        <div className="wx-sa-kpis">
          <KpiCard label="WORKERS" value={agent.workerCount ?? 0} />
          <KpiCard label="INVITES" value={agent.inviteCount ?? 0} />
        </div>

        <div className="wx-loc-policy-note">
          <strong>Invite Card สร้างได้เฉพาะ Agent นี้เท่านั้น</strong>
          <span>PLC / บมจ และ Super Agent ดูสถานะได้ แต่สร้างหรือส่ง Invite Card แทน Agent ไม่ได้</span>
        </div>

        <div className="wx-loc-policy-note">
          <strong>สมัครด่วนยังไม่ใช่พร้อม match งาน</strong>
          <span>ผู้สมัครต้องผ่านการยืนยัน e-KYC ก่อน จึงจะพร้อม match งานได้</span>
        </div>

        <section className="wx-panel wx-loc-chart-panel">
          <div className="wx-loc-panel-head">
            <h2 className="wx-panel-title">ผังความรับผิดชอบของ Agent</h2>
            <span className="wx-loc-panel-scope">Super Agent &gt; PLC / บมจ &gt; Agent &gt; กลุ่มแรงงาน</span>
          </div>
          <div className="wx-loc-chart-wrap">
            <div className="wx-tree-canvas">
              <div className="wx-tree-tier">
                <div className="wx-tree-row">
                  <div className="wx-orgchart-node wx-orgchart-node--root" aria-hidden="true">
                    <div className="wx-orgchart-node-title">{agent.displayName}</div>
                    <div className="wx-orgchart-node-code">{agent.agentCode}</div>
                    <div className="wx-node-card-metrics">
                      <div className="wx-node-card-metric">
                        <span className="wx-node-card-metric-value">{agent.workerCount ?? 0}</span>
                        <span className="wx-node-card-metric-label">แรงงาน</span>
                      </div>
                      <div className="wx-node-card-metric">
                        <span className="wx-node-card-metric-value">{agent.inviteCount ?? 0}</span>
                        <span className="wx-node-card-metric-label">คำเชิญ</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <span className="wx-tree-connector" aria-hidden="true" />

              <div className="wx-tree-tier">
                <div className="wx-tree-tier-label">กลุ่มแรงงาน (Worker Groups)</div>
                <button
                  type="button"
                  className="wx-agent-clickable-card wx-agent-clickable-card--reserved"
                  onClick={() => setSelectedCardKey("workerGroups")}
                >
                  <div className="wx-agent-reserved-card-title">กลุ่มแรงงานภายใต้ {agent.displayName}</div>
                  <div className="wx-agent-reserved-card-desc">การจัดกลุ่มแรงงานตามหน้างาน กะ หรือโซน</div>
                  <div className="wx-agent-reserved-card-note">ยังไม่เปิดใช้งาน</div>
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="wx-panel wx-loc-chart-panel">
          <div className="wx-loc-panel-head">
            <h2 className="wx-panel-title">ขั้นตอนคำเชิญ (Invite Pipeline)</h2>
            <span className="wx-loc-panel-scope">Invite created &gt; Quick register &gt; e-KYC pending &gt; Ready for match</span>
          </div>
          <div className="wx-agent-pipeline">
            <button
              type="button"
              className="wx-agent-clickable-card"
              onClick={() => setSelectedCardKey("pipelineInvited")}
            >
              <span className="wx-agent-pipeline-label">คำเชิญที่สร้างแล้ว</span>
              <span className="wx-agent-pipeline-value">{agent.inviteCount ?? 0}</span>
            </button>
            {PIPELINE_RESERVED_CARDS.map((stage) => (
              <button
                type="button"
                key={stage.key}
                className="wx-agent-clickable-card wx-agent-clickable-card--reserved"
                onClick={() => setSelectedCardKey(stage.key)}
              >
                <span className="wx-agent-pipeline-label">{stage.label}</span>
                <span className="wx-agent-pipeline-reserved">ยังไม่เปิดใช้งาน</span>
              </button>
            ))}
          </div>
        </section>

        <section className="wx-panel wx-loc-chart-panel">
          <div className="wx-loc-panel-head">
            <h2 className="wx-panel-title">ข้อมูลเพิ่มเติมของ Agent นี้</h2>
            <span className="wx-loc-panel-scope">ยังไม่เปิดใช้งาน</span>
          </div>
          <div className="wx-agent-reserved-grid">
            {RESERVED_CARDS.map((card) => (
              <button
                type="button"
                key={card.key}
                className="wx-agent-clickable-card wx-agent-clickable-card--reserved"
                onClick={() => setSelectedCardKey(card.key)}
              >
                <div className="wx-agent-reserved-card-title">{card.title}</div>
                <div className="wx-agent-reserved-card-desc">{card.description}</div>
                <div className="wx-agent-reserved-card-note">ยังไม่เปิดใช้งาน</div>
              </button>
            ))}
          </div>
        </section>
      </div>

      <DetailDrawer
        open={cardContent !== null}
        onClose={() => setSelectedCardKey(null)}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector="button.wx-agent-clickable-card"
      >
        {cardContent && (
          <ReservedInfoDrawer
            mark={cardContent.mark}
            title={cardContent.title}
            description={cardContent.description}
            apiName={cardContent.apiName}
            titleId={DRAWER_TITLE_ID}
          />
        )}
      </DetailDrawer>
    </AppShell>
  );
}

function KpiCard({ label, value, reservedNote }) {
  return (
    <div className={`wx-sa-kpi${reservedNote ? " wx-sa-kpi--reserved" : ""}`}>
      <span className="wx-sa-kpi-label">{label}</span>
      {reservedNote ? (
        <span className="wx-sa-kpi-reserved">{reservedNote}</span>
      ) : (
        <strong className="wx-sa-kpi-value">{value}</strong>
      )}
    </div>
  );
}

function ReservedInfoDrawer({ mark, title, description, apiName, titleId }) {
  return (
    <div className="wx-sa-inspector-body">
      <div className="wx-sa-inspector-head">
        <span className="wx-badge wx-badge--neutral">{mark}</span>
        <div>
          <h2 id={titleId} className="wx-sa-inspector-name">
            {title}
          </h2>
        </div>
      </div>

      <p className="wx-state-body wx-loc-inspector-description">{description}</p>

      {/* This drawer is a real feature: clicking a not-yet-built card explains
          in plain words what it will hold. What it used to add underneath that
          explanation was "รอเชื่อมต่อ <X> API" - the endpoint's name. The
          explanation is the useful half and it stays; the endpoint name does
          not, here or on the cards themselves. */}
      <p className="wx-sa-reserved-inline">ยังไม่เปิดใช้งาน</p>
    </div>
  );
}
